import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileText, Trash2, Eye, Pencil, Check, X } from "lucide-react";
import api, { apiError, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DocPreview from "@/components/DocPreview";
import { fmtDateTime } from "@/lib/format";

export default function DocSlot({ projectId, linkedTo, category, label, docs, onUploaded }) {
  const { readOnly } = useAuth();
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [descDraft, setDescDraft] = useState("");
  const items = (docs || []).filter((d) => d.linked_to === linkedTo);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("linked_to", linkedTo);
      await api.post(`/projects/${projectId}/documents`, fd);
      toast.success(`${label} uploaded`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Attachment deleted");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveDesc = async (d) => {
    try {
      await api.patch(`/documents/${d.id}`, { description: descDraft });
      setEditingId(null);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid={`docslot-${linkedTo}`} className="rounded-md border border-gray-200 bg-[#faf9f8] px-3 py-2.5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#d9cde5] bg-[#f3eff7] text-[#604184]"><FileText size={14} strokeWidth={1.8} /></span>
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-700">{label}</div>
            {items.length === 0 && <div className="text-[11px] text-gray-400">No documents attached</div>}
            {items.length > 0 && <div className="text-[11px] text-gray-500">{items.length} document{items.length > 1 ? "s" : ""}</div>}
          </div>
        </div>
        {!readOnly && (
          <div className="shrink-0">
            <input ref={inputRef} data-testid={`docslot-${linkedTo}-file-input`} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            <button
              data-testid={`docslot-${linkedTo}-upload`}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="bdx-icon-btn disabled:opacity-50"
              title={items.length ? "Upload another document" : "Upload document"}
            >
              <Upload size={14} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100 border-t border-gray-200" data-testid={`docslot-${linkedTo}-list`}>
          {items.map((d) => (
            <li key={d.id} className="flex items-start justify-between gap-2 py-1.5" data-testid={`docslot-doc-${d.id}`}>
              <div className="min-w-0">
                <button
                  data-testid={`docslot-doc-${d.id}-open`}
                  className="block max-w-full truncate text-left font-mono-data text-[11px] font-medium text-gray-800 hover:text-[#604184] hover:underline"
                  title={`View ${d.filename}`}
                  onClick={() => setPreview(d)}
                >
                  {d.filename}
                </button>
                <div className="text-[10px] text-gray-500">{fmtDateTime(d.uploaded_at)} · {d.uploaded_by}</div>
                {editingId === d.id ? (
                  <div className="mt-1 flex items-center gap-1">
                    <input
                      data-testid={`docslot-doc-${d.id}-desc-input`}
                      className="w-52 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px]"
                      value={descDraft}
                      placeholder="Description / note"
                      maxLength={200}
                      autoFocus
                      onChange={(e) => setDescDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveDesc(d); if (e.key === "Escape") setEditingId(null); }}
                    />
                    <button data-testid={`docslot-doc-${d.id}-desc-save`} className="bdx-icon-btn h-6 w-6" title="Save" onClick={() => saveDesc(d)}><Check size={12} strokeWidth={1.8} /></button>
                    <button className="bdx-icon-btn h-6 w-6" title="Cancel" onClick={() => setEditingId(null)}><X size={12} strokeWidth={1.8} /></button>
                  </div>
                ) : (
                  d.description && <div className="mt-0.5 text-[10px] italic text-gray-500" data-testid={`docslot-doc-${d.id}-desc`}>{d.description}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <button data-testid={`docslot-doc-${d.id}-view`} className="bdx-icon-btn h-7 w-7" title="View" onClick={() => setPreview(d)}>
                  <Eye size={13} strokeWidth={1.6} />
                </button>
                <button data-testid={`docslot-doc-${d.id}-download`} className="bdx-icon-btn h-7 w-7" title="Download" onClick={() => downloadBlob(`/documents/${d.id}/download`, d.filename)}>
                  <Download size={13} strokeWidth={1.6} />
                </button>
                {!readOnly && (
                  <>
                    <button data-testid={`docslot-doc-${d.id}-edit`} className="bdx-icon-btn h-7 w-7" title="Edit description" onClick={() => { setEditingId(d.id); setDescDraft(d.description || ""); }}>
                      <Pencil size={13} strokeWidth={1.6} />
                    </button>
                    <button data-testid={`docslot-doc-${d.id}-delete`} className="bdx-icon-btn-danger h-7 w-7" title="Delete attachment" onClick={() => remove(d)}>
                      <Trash2 size={13} strokeWidth={1.6} />
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
